import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';

export interface TurnstileCallbacks {
  success: (token: string) => void;
  expired: () => void;
  error: () => void;
}

interface TurnstileApi {
  ready(callback: () => void): void;
  render(element: HTMLElement, options: {
    sitekey: string;
    action: string;
    callback: (token: string) => void;
    'expired-callback': () => void;
    'error-callback': () => void;
  }): string;
  reset(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

@Injectable({ providedIn: 'root' })
export class TurnstileService {
  private readonly siteKey = environment.turnstileSiteKey;
  private readonly action = environment.turnstileAction;
  private apiPromise: Promise<TurnstileApi> | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  load(): Promise<TurnstileApi> {
    if (!this.isBrowser()) {
      return Promise.reject(new Error('Turnstile is unavailable during server rendering.'));
    }

    if (this.apiPromise) {
      return this.apiPromise;
    }

    this.apiPromise = new Promise<TurnstileApi>((resolve, reject) => {
      const waitForApi = (): void => {
        if (window.turnstile) {
          window.turnstile.ready(() => resolve(window.turnstile as TurnstileApi));
          return;
        }

        globalThis.setTimeout(waitForApi, 50);
      };

      const script = document.querySelector<HTMLScriptElement>(
        'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
      );

      if (!script) {
        reject(new Error('Turnstile client script is not loaded.'));
        return;
      }

      script.addEventListener('error', () => reject(new Error('Turnstile failed to load.')), { once: true });
      waitForApi();
    });

    return this.apiPromise;
  }

  async render(element: HTMLElement, callbacks: TurnstileCallbacks): Promise<string> {
    const api = await this.load();

    return api.render(element, {
      sitekey: this.siteKey,
      action: this.action,
      callback: callbacks.success,
      'expired-callback': callbacks.expired,
      'error-callback': callbacks.error,
    });
  }

  reset(widgetId: string | null): void {
    if (widgetId && this.isBrowser() && window.turnstile) {
      window.turnstile.reset(widgetId);
    }
  }
}
